import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { getListEmergencyLightChecksQueryControllerListQueryKey } from "@/generated/emergency-light-checks/emergency-light-checks";
import type { EmergencyLightCheckKind } from "@/lib/emergency-light-checks";
import { offlineDb } from "@/lib/offline/db";
import { isPermanentlyFailed, syncService } from "@/lib/offline/syncService";

export interface PendingEmergencyLightCheck {
  id: string;
  queuedAt: string;
  itemCount: number;
  failedCount: number;
  /**
   * `rejected`: the server refused it and will again, so it can only be
   * discarded. `stalled`: the queue ran out of retries without an answer, so
   * it can be retried.
   */
  localSyncState: "pending" | "rejected" | "stalled";
  /** The server's reason, when it rejected the check. */
  lastError?: string;
  /** Who recorded it on this device. */
  capturedByUserId?: string;
}

interface UsePendingEmergencyLightChecksResult {
  checks: PendingEmergencyLightCheck[];
  /** Drop a rejected check from the device: its local row and its queue item. */
  discard: (localId: string) => Promise<void>;
  /** Send stalled checks again. */
  retry: () => Promise<void>;
}

/**
 * The emergency lighting checks this device has recorded but the server does
 * not hold yet, newest first like the history they are shown in. Every submit
 * goes through the sync queue, so without this a check queued offline is
 * invisible until it lands, and one the server rejected is never seen at all.
 */
export function usePendingEmergencyLightChecks(
  nurseryId: string | null,
  kind: EmergencyLightCheckKind,
): UsePendingEmergencyLightChecksResult {
  const queryClient = useQueryClient();
  const [checks, setChecks] = useState<PendingEmergencyLightCheck[]>([]);
  const pendingIds = useRef<Set<string>>(new Set());
  const loadCount = useRef(0);

  const loadLocal = useCallback(async () => {
    const load = ++loadCount.current;
    if (!nurseryId) {
      pendingIds.current = new Set();
      setChecks([]);
      return;
    }
    const rows = await offlineDb.emergencyLightChecks
      .where("nurseryId")
      .equals(nurseryId)
      .filter((c) => c.kind === kind && c.syncState !== "synced")
      .toArray();
    // A check the queue gave up on will never land, even though its row still
    // reads pending.
    const givenUp = new Set(
      (
        await offlineDb.syncQueue
          .filter((i) => i.type === "emergencyLightCheck")
          .toArray()
      )
        .filter(isPermanentlyFailed)
        .map((i) => i.entityId),
    );
    // A later load (a nursery or kind switch) has superseded this one.
    if (load !== loadCount.current) return;

    // A row that left the list landed on the server; the history only refetches
    // once the whole monitoring queue drains, so fetch it now.
    const ids = new Set(rows.map((row) => row.id));
    const landed = [...pendingIds.current].some((id) => !ids.has(id));
    pendingIds.current = ids;
    if (landed) {
      void queryClient.invalidateQueries({
        queryKey:
          getListEmergencyLightChecksQueryControllerListQueryKey(nurseryId),
      });
    }

    setChecks(
      rows
        .sort((a, b) => b.queuedAt.localeCompare(a.queuedAt))
        .map((row) => ({
          id: row.id,
          queuedAt: row.queuedAt,
          itemCount: row.items.length,
          failedCount: row.items.filter((item) => !item.passed).length,
          localSyncState: localSyncStateOf(row.syncState, givenUp.has(row.id)),
          lastError: row.lastError,
          capturedByUserId: row.capturedByUserId,
        })),
    );
  }, [nurseryId, kind, queryClient]);

  useEffect(() => {
    void loadLocal();
  }, [loadLocal]);

  // Re-read on every sync notification: rows leave as they land, and a
  // rejection or a spent retry flips a row to failed mid-run.
  useEffect(() => {
    const unsubscribe = syncService.subscribe(() => {
      void loadLocal();
    });
    return () => {
      unsubscribe();
    };
  }, [loadLocal]);

  const discard = useCallback(
    async (localId: string) => {
      await offlineDb.syncQueue
        .filter(
          (i) => i.type === "emergencyLightCheck" && i.entityId === localId,
        )
        .delete();
      await offlineDb.emergencyLightChecks.delete(localId);
      await loadLocal();
    },
    [loadLocal],
  );

  const retry = useCallback(async () => {
    await syncService.retryFailed();
    await loadLocal();
  }, [loadLocal]);

  return { checks, discard, retry };
}

function localSyncStateOf(
  syncState: string,
  givenUp: boolean,
): PendingEmergencyLightCheck["localSyncState"] {
  if (syncState === "error") return "rejected";
  if (givenUp) return "stalled";
  return "pending";
}
